import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/common/Pagination';
import { Search, Eye, Trash2, ArrowRight } from 'lucide-react';
import { API_BASE_URL, PREVIEW_OFFLINE } from '../../config';
import useAppStore from '../../store/useAppStore';
import { useAppContext } from '../../hooks/useAppContext';
import ModernAdminQuotationsScreen from './ModernAdminQuotationsScreen';

interface SavedQuotationSummary {
  id: string;
  quote_number: string;
  quote_date: string;
  customer_name: string;
  location: string | null;
  mobile: string | null;
  created_at: string;
  total_with_tax: number;
}

const ITEMS_PER_PAGE = 10;

const AdminQuotationsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { openConfirmation } = useAppStore();
  const { designMode } = useAppContext();

  const [items, setItems] = useState<SavedQuotationSummary[]>([]);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  if (designMode === 'modern') {
    return <ModernAdminQuotationsScreen />;
  }

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        if (PREVIEW_OFFLINE) return;
        const res = await fetch(`${API_BASE_URL}/quotations?limit=60`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setItems(data as SavedQuotationSummary[]);
        else if (Array.isArray((data as any).items)) setItems(((data as any).items) as SavedQuotationSummary[]);
      } catch { }
    };
    fetchQuotations();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(it => {
      const matchSearch = q === '' || [it.customer_name, it.quote_number, it.location || '', it.mobile || ''].some(v => String(v || '').toLowerCase().includes(q));
      const matchDate = dateFilter === '' || new Date(it.quote_date).toISOString().slice(0, 10) === dateFilter;
      return matchSearch && matchDate;
    });
  }, [items, search, dateFilter]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;

  const deleteQuotation = (id: string) => {
    openConfirmation('هل أنت متأكد من حذف هذا العرض؟ لا يمكن التراجع عن هذا الإجراء.', async () => {
      try {
        if (PREVIEW_OFFLINE) return;
        const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
        if (res.status === 204) {
          setItems(prev => prev.filter(x => String(x.id) !== String(id)));
        }
      } catch { }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">عروض السعر</h1>
        <Button onClick={() => navigate('/')} variant="secondary">
          <ArrowRight size={16} className="me-2" />
          رجوع
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>بحث وفلترة</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Input placeholder="بحث ذكي..." icon={<Search size={16} />} value={search} onChange={e => setSearch(e.target.value)} />
            <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          {paginated.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginated.map(it => (
                <Card key={it.id} className="transition-all duration-300 hover:shadow-lg hover:-translate-y-[2px]">
                  <CardHeader className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base truncate">{it.customer_name}</CardTitle>
                      <p className="text-xs text-slate-500">{new Date(it.quote_date || it.created_at).toLocaleDateString('ar-SA')} • {it.quote_number}</p>
                    </div>
                    <div className="text-sm font-bold text-report-project">
                      {Number(it.total_with_tax || 0).toLocaleString('ar-EG')} ر.س
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {it.location && (<p className="text-slate-600 dark:text-slate-300">الموقع: {it.location}</p>)}
                    {it.mobile && (<p className="text-slate-600 dark:text-slate-300">الجوال: {it.mobile}</p>)}
                  </CardContent>
                  <CardFooter className="flex items-center justify-end gap-2 border-t border-slate-200 dark:border-slate-600">
                    <Button variant="ghost" size="sm" className="p-2 h-auto" title="عرض" onClick={() => navigate(`/quotations/${it.id}`)}><Eye size={16} /></Button>
                    <Button variant="ghost" size="sm" className="p-2 h-auto text-destructive hover:bg-destructive/10" title="حذف" onClick={() => deleteQuotation(it.id)}><Trash2 size={16} /></Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-slate-500">لا توجد عروض تطابق معايير البحث.</p>
          )}
        </CardContent>
        {totalPages > 1 && (
          <CardFooter className="flex justify-between items-center">
            <span className="text-sm text-slate-500">عرض {paginated.length} من {filtered.length} عرض</span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default AdminQuotationsScreen;
